import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {channelOf, IChatMessage, IDeletedEvent, IReactionsEvent, IThread, MESSAGES_PAGE} from "./types";
import {
    fetchDeleteMessage,
    fetchEditMessage,
    fetchMessages,
    fetchSendMessage,
    fetchThreads,
    fetchToggleReaction,
    fetchUnread,
} from "./actions";

export interface IChannelState {
    /** По времени ↑: новые — последними. */
    messages: IChatMessage[];
    hasMore: boolean;
    loading: boolean;
    loaded: boolean;
}

export interface IChatState {
    channels: Record<string, IChannelState>;
    /** Непрочитанное по каналам: `general` и uuid машин. */
    unread: Record<string, number>;
    /** Канал, тред которого сейчас на экране (вкладка «Обсуждение» или общий чат). */
    active: string | null;
    /** Выезжающая панель в режиме общего чата. */
    generalOpen: boolean;
    /** «Обсуждения» (S11): треды машин, свежие сверху. */
    threads: IThread[];
    threadsOpen: boolean;
}

export const initialState: IChatState = {
    channels: {},
    unread: {},
    active: null,
    generalOpen: false,
    threads: [],
    threadsOpen: false,
};

const emptyChannel = (): IChannelState => ({messages: [], hasMore: false, loading: false, loaded: false});

const byTime = (a: IChatMessage, b: IChatMessage) =>
    Date.parse(a.created_at) - Date.parse(b.created_at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/**
 * Вставить или заменить сообщение в канале, который загружен или грузится:
 * пришедшее по сокету во время первой загрузки сольётся с её ответом.
 */
const upsert = (state: IChatState, m: IChatMessage) => {
    const ch = state.channels[channelOf(m)];
    if (!ch || (!ch.loaded && !ch.loading)) return;
    const i = ch.messages.findIndex((x) => x.id === m.id);
    if (i >= 0) ch.messages[i] = m;
    else {
        ch.messages.push(m);
        ch.messages.sort(byTime);
    }
};

export const ChatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        /** `chat:message`, `chat:message_updated` и ответы отправки / правки. */
        messageUpserted(state, action: PayloadAction<IChatMessage>) {
            upsert(state, action.payload);
        },
        /** `chat:message_deleted` и ответ удаления. */
        messageDeleted(state, action: PayloadAction<IDeletedEvent>) {
            const ch = state.channels[channelOf(action.payload)];
            if (ch) ch.messages = ch.messages.filter((m) => m.id !== action.payload.id);
        },
        /** `chat:reactions` и ответы поставить / снять. */
        reactionsReplaced(state, action: PayloadAction<IReactionsEvent>) {
            const ch = state.channels[channelOf(action.payload)];
            const m = ch?.messages.find((x) => x.id === action.payload.message_id);
            if (m) m.reactions = action.payload.reactions;
        },
        unreadIncremented(state, action: PayloadAction<string>) {
            state.unread[action.payload] = (state.unread[action.payload] ?? 0) + 1;
        },
        unreadCleared(state, action: PayloadAction<string>) {
            delete state.unread[action.payload];
        },
        setActiveChannel(state, action: PayloadAction<string | null>) {
            state.active = action.payload;
        },
        setGeneralOpen(state, action: PayloadAction<boolean>) {
            state.generalOpen = action.payload;
        },
        setThreadsOpen(state, action: PayloadAction<boolean>) {
            state.threadsOpen = action.payload;
        },
        /** После переподключения: каналы перечитываются при следующем открытии. */
        channelsReset(state) {
            state.channels = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchMessages.pending, (state, action) => {
            const {channel, older} = action.meta.arg;
            const ch = (state.channels[channel] ??= emptyChannel());
            ch.loading = true;
            if (!older) ch.loaded = ch.loaded && ch.messages.length > 0;
        });
        builder.addCase(fetchMessages.fulfilled, (state, action) => {
            const {channel, older, items} = action.payload;
            const ch = (state.channels[channel] ??= emptyChannel());
            if (older) {
                const known = new Set(ch.messages.map((m) => m.id));
                ch.messages = [...items.filter((m) => !known.has(m.id)), ...ch.messages];
            } else {
                // Свежая страница: сообщения, пришедшие по сокету во время запроса, не терять.
                const fresh = new Set(items.map((m) => m.id));
                const last = items[items.length - 1];
                const newer = ch.messages.filter((m) => !fresh.has(m.id) && (!last || byTime(m, last) > 0));
                ch.messages = [...items, ...newer];
            }
            ch.hasMore = items.length >= MESSAGES_PAGE;
            ch.loading = false;
            ch.loaded = true;
        });
        builder.addCase(fetchMessages.rejected, (state, action) => {
            const ch = state.channels[action.meta.arg.channel];
            if (ch) ch.loading = false;
        });
        builder.addCase(fetchSendMessage.fulfilled, (state, action) => upsert(state, action.payload));
        builder.addCase(fetchThreads.fulfilled, (state, action) => {
            state.threads = action.payload;
        });
        builder.addCase(fetchEditMessage.fulfilled, (state, action) => upsert(state, action.payload));
        builder.addCase(fetchDeleteMessage.fulfilled, (state, action) => {
            const ch = state.channels[channelOf(action.payload)];
            if (ch) ch.messages = ch.messages.filter((m) => m.id !== action.payload.id);
        });
        builder.addCase(fetchToggleReaction.fulfilled, (state, action) => {
            const ch = state.channels[channelOf(action.payload)];
            const m = ch?.messages.find((x) => x.id === action.payload.message_id);
            if (m) m.reactions = action.payload.reactions;
        });
        builder.addCase(fetchUnread.fulfilled, (state, action) => {
            // Открытый и видимый канал thunk уже исключил.
            state.unread = action.payload;
        });
    },
});

export const {
    messageUpserted,
    messageDeleted,
    reactionsReplaced,
    unreadIncremented,
    unreadCleared,
    setActiveChannel,
    setGeneralOpen,
    setThreadsOpen,
    channelsReset,
} = ChatSlice.actions;

export default ChatSlice.reducer;
